import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import Save from '@mui/icons-material/Save';
import axios from 'axios';
import { url } from '../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PERMISSION_LABELS = {
  canCreateUser: 'Criar usuário',
  canEditFeeSettings: 'Editar taxas',
  canResetFeeSettings: 'Resetar taxas',
  canEditProfiles: 'Editar perfis (dados e status)',
  canViewLogs: 'Ver logs de alterações',
  canUploadLogo: 'Upload de logo',
  canRefreshPaymentStatus: 'Atualizar status de pagamento',
  canViewReport: 'Ver relatório de pagamentos',
};

const PERMISSIONS_ADMIN = [
  'canCreateUser',
  'canEditFeeSettings',
  'canResetFeeSettings',
  'canEditProfiles',
  'canViewLogs',
  'canUploadLogo',
  'canRefreshPaymentStatus',
  'canViewReport',
];

const PERMISSIONS_USER = ['canRefreshPaymentStatus', 'canViewReport'];

const defaultPermissions = () => ({
  ADMIN: PERMISSIONS_ADMIN.reduce((acc, k) => ({ ...acc, [k]: true }), {}),
  USER: PERMISSIONS_USER.reduce((acc, k) => ({ ...acc, [k]: true }), {}),
});

export default function ParametrosList({ usePageTitle }) {
  usePageTitle?.('PixTick | Parâmetros por nível');
  const [permissionsByRole, setPermissionsByRole] = useState(defaultPermissions());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') {
      navigate('/relatorio-pagamento');
      return;
    }
    async function fetchPermissions() {
      try {
        setError(null);
        const token = localStorage.getItem('token');
        const { data } = await axios.get(url + '/api/v1/role-permissions', {
          headers: { Authorization: 'Bearer ' + token },
        });
        if (data?.data && typeof data.data === 'object') {
          const merged = defaultPermissions();
          Object.keys(merged).forEach((role) => {
            merged[role] = { ...merged[role], ...(data.data[role] || {}) };
          });
          setPermissionsByRole(merged);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          logout();
          navigate('/signin');
        } else {
          setError('Erro ao carregar parâmetros.');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchPermissions();
  }, [user?.role, navigate, logout]);

  const handleToggle = (role, key, value) => {
    setPermissionsByRole((prev) => ({
      ...prev,
      [role]: { ...prev[role], [key]: value },
    }));
    setSuccess(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(url + '/api/v1/role-permissions', { permissionsByRole }, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      });
      setSuccess('Parâmetros salvos com sucesso.');
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate('/signin');
      } else {
        setError('Erro ao salvar parâmetros.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'SUPER_ADMIN') return null;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ marginTop: '80px', pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976D2', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings /> Parâmetros por nível
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Defina quais ações cada nível (Admin / Usuário) pode realizar. Super Admin não é afetado.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>Admin</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permissões para usuários com nível Admin (empresa).
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {PERMISSIONS_ADMIN.map((key) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={!!permissionsByRole.ADMIN?.[key]}
                  onChange={(_, v) => handleToggle('ADMIN', key, v)}
                  color="primary"
                />
              }
              label={PERMISSION_LABELS[key] || key}
            />
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" color="primary" gutterBottom>Usuário</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permissões para usuários com nível Usuário (vendedores).
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {PERMISSIONS_USER.map((key) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={!!permissionsByRole.USER?.[key]}
                  onChange={(_, v) => handleToggle('USER', key, v)}
                  color="primary"
                />
              }
              label={PERMISSION_LABELS[key] || key}
            />
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando…' : 'Salvar parâmetros'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
